// Copyright Abridged, Inc. 2021,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {LetsGoConfigModel} from '../models/lets-go-config.model.js';
import {Stats} from '../models/stats.model.js';
import {TokenPermissionedChat} from '../models/token-permissioned-chat.model.js';
import {TokenPermissionedChatToBeReplaced} from '../models/token-permissioned-chat-to-be-replaced.model.js';
import {NewTokenPermissionedChat} from '../models/new-token-permissioned-chat.model.js';
import {CommunityPartial} from '../models/community-partial.model.js';
import {Community} from '../models/community.model.js';
import {NewCommunity} from '../models/new-community.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by CommunityController.
 */
export interface CommunityService {
  /**
   * @param id
   * @param _requestBody
   */
  updateAdmins(id: string, _requestBody: string[]): Promise<unknown>;

  /**
   * @param id
   * @param _requestBody
   * @returns OK
   */
  letsGoConfig(
    id: string,
    _requestBody: LetsGoConfigModel,
  ): Promise<LetsGoConfigModel>;

  /**
   * @param id
   * @returns OK
   */
  getLetsGoConfig(id: string): Promise<LetsGoConfigModel>;

  /**
   * @param communityPK
   */
  triggerBalanceCheck(communityPK: string): Promise<unknown>;

  /**
   * @param id
   */
  getPremiumSupportUrl(id: string): Promise<unknown>;

  /**
   * @param id
   * @returns OK
   */
  getStats(id: string): Promise<Stats>;

  /**
   * @param id
   * @returns Result set of TokenPermissionedChat model instances
   */
  getGuildTgrInfo(id: string): Promise<{
    paginationToken?: string;
    items?: TokenPermissionedChat[];
  }>;

  /**
   * @param id
   * @returns Result set of TokenPermissionedChat model instances
   */
  getCommunityTgRs(id: string): Promise<{
    paginationToken?: string;
    items?: TokenPermissionedChat[];
  }>;

  /**
   * @param id
   */
  getBackgroundCheckStatus(id: string): Promise<unknown>;

  /**
   * @param id
   * @param tpcId
   * @param _requestBody
   */
  replaceTpc(
    id: string,
    tpcId: string,
    _requestBody: TokenPermissionedChatToBeReplaced,
  ): Promise<unknown>;

  /**
   * @param id
   * @param tpcId
   */
  deleteTpc(id: string, tpcId: string): Promise<unknown>;

  /**
   * @param id
   * @param _requestBody
   * @returns OK
   */
  createTpc(
    id: string,
    _requestBody: NewTokenPermissionedChat,
  ): Promise<TokenPermissionedChat>;

  /**
   * @param id
   * @param limit
   * @param paginationToken
   * @returns Result set of TokenPermissionedChat model instances
   */
  findTpcs(
    id: string,
    limit?: number,
    paginationToken?: string,
  ): Promise<{
    paginationToken?: string;
    items?: TokenPermissionedChat[];
  }>;

  /**
   * @param id
   * @param _requestBody
   */
  replaceById(id: string, _requestBody: {}): Promise<unknown>;

  /**
   * @param id
   * @param _requestBody
   */
  updateById(id: string, _requestBody: CommunityPartial): Promise<unknown>;

  /**
   * @param id
   * @param include
   * @returns OK
   */
  findById(id: string, include?: string): Promise<Community>;

  /**
   * @param id
   */
  deleteById(id: string): Promise<unknown>;

  /**
   * @param _requestBody
   * @returns Community model instance
   */
  create(_requestBody: NewCommunity): Promise<Community>;

  /**
   * @param limit
   * @param paginationToken
   * @param include
   * @returns Result set of Community model instances
   */
  find(
    limit?: number,
    paginationToken?: string,
    include?: string,
  ): Promise<{
    paginationToken?: string;
    items?: Community[];
  }>;
}

export class CommunityServiceProvider implements Provider<CommunityService> {
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<CommunityService> {
    const service = await getService<{
      apis: {CommunityController: CommunityService};
    }>(this.dataSource);
    return service.apis['CommunityController'];
  }
}
